"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import ServiceFormSuccess from "@/components/service/ServiceFormSuccess";
import { inputClass, selectClass, labelClass, submitClass, formCardClass } from "@/components/service/formStyles";
const SERVICES = ["Fascias", "Soffits", "Guttering", "Downpipes", "Gutter Cleaning", "Repairs & Maintenance"];
const PROPERTY_TYPES = ["Detached House", "Semi-Detached House", "Terraced House", "Bungalow", "Flat / Apartment", "Commercial Property"];
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <ServiceFormSuccess title="Request Received" message={successMessage || "Thank you! We'll contact you shortly with your fascias quote."} onReset={resetForm} resetLabel="Submit Another Request" />;
  }
  return <Form onSubmit={handleSubmit} className={formCardClass}>
      <FormField control={control} name="service_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Service Required</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={selectClass}>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {SERVICES.map((service, i) => <SelectItem key={service} value={service} data-index={i}>
                    {service}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="property_type" rules={{
        required: "Please select a property type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Property Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={selectClass}>
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROPERTY_TYPES.map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="postcode" rules={{
        required: "Postcode is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Postcode</FormLabel>
              <FormControl>
                <Input placeholder="e.g. SO15 1AA" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name is too short"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClass}>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className={inputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="07700 900000" className={inputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className={submitClass}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : "Get My Free Quote"}
      </Button>
    </Form>;
}
export default function GetAFreeFasciasQuote() {
  return <FormProvider formId="start_project_form_get_in_touch" sectionName="Get a Free Fascias Quote" defaultValues={{
    service_type: "",
    property_type: "",
    postcode: "",
    name: "",
    email: "",
    phone: ""
  }} successMessage="Thank you! We'll contact you shortly with your fascias quote.">
      <QuoteForm />
    </FormProvider>;
}